"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CategoryNode } from "@/lib/catalog";
import { fixMaybeBrokenText } from "@/lib/text-client";

type CatalogMegaNode = CategoryNode & {
  previewImage: string;
  children: CatalogMegaNode[];
};

const TEXT = {
  openCatalog: "Открыть каталог",
  closeCatalog: "Закрыть каталог",
  catalog: "Каталог",
  fullCatalog: "Весь каталог",
  sections: "Разделы каталога",
  subcategories: "Подкатегории",
  openSection: "Открыть раздел"
} as const;

function CatalogIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path d="M4 7h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
      <path d="M4 12h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
      <path d="M4 17h16" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="2" />
    </svg>
  );
}

export function CatalogMegaMenuClient({ roots }: { roots: CatalogMegaNode[] }) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeSlug, setActiveSlug] = useState(roots[0]?.slug ?? "");

  const activeRoot = useMemo(
    () => roots.find((root) => root.slug === activeSlug) ?? roots[0] ?? null,
    [activeSlug, roots]
  );

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!shellRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  if (!roots.length) {
    return null;
  }

  const sectionNodes = activeRoot ? (activeRoot.children.length ? activeRoot.children : [activeRoot]) : [];

  return (
    <div className={`catalog-menu-v2 ${isOpen ? "catalog-menu-v2--open" : ""}`} ref={shellRef}>
      <button
        aria-expanded={isOpen}
        aria-label={TEXT.openCatalog}
        className="catalog-menu-v2__trigger"
        onClick={() => setIsOpen((current) => !current)}
        type="button"
      >
        <CatalogIcon />
        <span>{TEXT.catalog}</span>
      </button>

      {isOpen ? (
        <>
          <button
            aria-label={TEXT.closeCatalog}
            className="catalog-menu-v2__backdrop"
            onClick={() => setIsOpen(false)}
            type="button"
          />

          <div className="catalog-menu-v2__panel">
            <aside className="catalog-menu-v2__sidebar">
              <div className="catalog-menu-v2__sidebar-title">{TEXT.sections}</div>

              <div className="catalog-menu-v2__root-list">
                {roots.map((root) => (
                  <button
                    className={`catalog-menu-v2__root ${root.slug === activeRoot?.slug ? "is-active" : ""}`}
                    key={root.slug}
                    onClick={() => setActiveSlug(root.slug)}
                    type="button"
                  >
                    <Image alt="" height={44} sizes="44px" src={root.previewImage || "/brand-logo.png"} width={44} />

                    <span>
                      <strong>{fixMaybeBrokenText(root.name)}</strong>
                      <em>{root.productCount.toLocaleString("ru-RU")} товаров</em>
                    </span>
                  </button>
                ))}
              </div>
            </aside>

            {activeRoot ? (
              <div className="catalog-menu-v2__content">
                <div className="catalog-menu-v2__content-head">
                  <div>
                    <span className="catalog-menu-v2__eyebrow">{TEXT.subcategories}</span>

                    <Link
                      className="catalog-menu-v2__title"
                      href={`/category/${activeRoot.slug}`}
                      onClick={() => setIsOpen(false)}
                    >
                      {fixMaybeBrokenText(activeRoot.name)}
                    </Link>

                    <p>{activeRoot.productCount.toLocaleString("ru-RU")} товаров в разделе</p>
                  </div>

                  <Link
                    className="catalog-menu-v2__all-link"
                    href={`/category/${activeRoot.slug}`}
                    onClick={() => setIsOpen(false)}
                  >
                    {TEXT.openSection}
                  </Link>
                </div>

                <div className="catalog-menu-v2__tiles">
                  {sectionNodes.map((child) => (
                    <Link
                      className="catalog-menu-v2__tile"
                      href={`/category/${child.slug}`}
                      key={child.slug}
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="catalog-menu-v2__tile-media">
                        <Image
                          alt={fixMaybeBrokenText(child.name)}
                          height={112}
                          sizes="112px"
                          src={child.previewImage || "/brand-logo.png"}
                          width={112}
                        />
                      </span>

                      <span className="catalog-menu-v2__tile-copy">
                        <strong>{fixMaybeBrokenText(child.name)}</strong>
                        <em>{child.productCount.toLocaleString("ru-RU")} товаров</em>
                      </span>
                    </Link>
                  ))}
                </div>

                <div className="catalog-menu-v2__footer">
                  <Link href="/catalog" onClick={() => setIsOpen(false)}>
                    {TEXT.fullCatalog}
                  </Link>
                </div>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}
